import React, {useState} from 'react'
import {Col, Row} from 'components/Display'
import {Optional, ProblemSolution, RouteMatch} from 'data/types'
import backArrow from 'assets/left-arrow-back.svg'
import Portal from 'components/Portal'
import services from 'services'
import SumarizedProblem from './SumarizedProblem'
import CommentSection from './CommentSection'
import CommentChatHeader from 'components/CommentChatHeader'
import CommentInput from 'views/CommentInput'
import getImageSrcFromBlob from 'utils/blob'

type SolutionDetailsProps = {
  match: RouteMatch<{
    problemId: string
    solutionId: string
  }>
}

function FullScreenSolution(props: {solution: ProblemSolution; isOpen: boolean; close: () => void; imageUrl: string}) {
  return props.isOpen ? (
    <Portal>
      <Col className={`absolute top-0 left-0 w-full h-full z-50 full-screen-problem bg-black`} onClick={props.close}>
        <Col className="h-20" />
        <img className="flex-1 h-10 w-auto object-contain" src={props.imageUrl} alt={props.solution.title} />
        <Col className="h-20" />
      </Col>
    </Portal>
  ) : null
}

export default function SolutionDetails(props: SolutionDetailsProps) {
  const pbService = services.getSingleProblemService(props.match.params.problemId)
  const [solutions] = pbService.solutions.hook()
  const [isFullScreen, setFullScreen] = useState(false)
  const solution: Optional<ProblemSolution> = solutions.find((s) => s.id.toString() === props.match.params.solutionId)
  const [problem] = pbService.problem.hook()

  if (problem == null) {
    return null
  }

  if (solution == null) {
    return null
  }

  return (
    <React.Fragment>
      <Col className="flex-1 overflow-y-scroll">
        <SumarizedProblem problem={problem} />
        <Row
          className="hidden sm:flex px-8 text-center items-center cursor-pointer"
          onClick={() => {
            services.router.selectSolutions(problem)
          }}
        >
          <img className="h-3 w-3 object-cover" alt={'backArrow'} src={backArrow} />
          <Row className="text-xs font-bold text-itx-gray-dark ml-2">Solutions</Row>
        </Row>
        <Row>
          <div className="text-itx-primary-darkest text-xl font-bold mt-4 px-8">{solution?.title}</div>
        </Row>
        <Row>
          {solution.description.length > 0 ? (
            <div className="h-23vh min-h-23vh text-itx-primary-darkest text-lg font-normal font-medium mt-4 px-8 break-words w-full">
              {solution?.description}
            </div>
          ) : (
            <div className="h-23vh text-itx-primary-darkest text-lg font-normal font-medium mt-4 px-8 break-words w-full"></div>
          )}
        </Row>
        <div>
          <Row className="pr-4 mt-10 px-8 flex-1">
            {solution.images.map((image) => {
              return (
                <React.Fragment key={image.id}>
                  <img
                    className="md:flex border rounded-lg h-32 w-64 object-cover"
                    alt={'placeholderAttachement'}
                    src={getImageSrcFromBlob(image)}
                    onClick={() => setFullScreen(true)}
                  />
                  <FullScreenSolution solution={solution} imageUrl={image.url} isOpen={isFullScreen} close={() => setFullScreen(false)} />
                </React.Fragment>
              )
            })}
          </Row>
        </div>
        <div className="bg-blue-itx-light mt-4 border-t flex-grow">
          <CommentSection
            threadId={solution.threadId}
            header={<CommentChatHeader user={solution.creator} i18nPlaceholder="solutions.createdThisSolution" createdAt={solution.createdAt} />}
          />
        </div>
      </Col>
      <div>
        <CommentInput threadId={solution.threadId} />
      </div>
    </React.Fragment>
  )
}
